// TanStack Start instance: global request middleware for every document request, server route and
// server function call. (Static files are served by Workers Assets before the Worker runs.)
import { createCsrfMiddleware, createMiddleware, createStart } from "@tanstack/react-start";
import { getRequestCookies } from "src/lib/RequestState";
import { registerBackendBinding } from "src/server/BackendBinding";
import { runSiteMiddleware } from "src/server/SiteMiddleware";

/** Site gate, bypass links, session refresh and role redirects (src/server/SiteMiddleware.ts). */
const siteMiddleware = createMiddleware().server(async ({ request, next }) => {
  // Before any backend request of this request (the session refresh, loaders, server functions).
  registerBackendBinding();
  const cookies = getRequestCookies();
  if (cookies) {
    const response = await runSiteMiddleware(request, cookies);
    if (response) return response as never;
  }
  return next();
});

/**
 * Server function calls over HTTP (`/_serverFn/*`) must come from this site's own pages: the browser's
 * `Sec-Fetch-Site` (or `Origin`, then `Referer`) has to say same-origin, otherwise 403. Server
 * functions act with the visitor's cookies (session, selected project), so a cross-site form or
 * fetch must not be able to trigger them. Loaders calling server functions during SSR don't go
 * through HTTP, and document requests and server routes (APIs such as `/openapi.yaml`) are not
 * affected. First, so a rejected call never reaches the session refresh.
 */
const csrfMiddleware = createCsrfMiddleware({
  filter: (context) => context.handlerType === "serverFn",
});

export const startInstance = createStart(() => ({
  requestMiddleware: [csrfMiddleware, siteMiddleware],
}));
